import { FC, useCallback, useMemo } from 'react';
import { Button, Card, Checkbox, RadixTable, TColumn } from '@features/ui';
import { ProductOffer } from '@src/types/productsAddons.ts';
import { ViewButton } from '@features/ui/components/view-button.tsx';
import { EyeV2Icon } from '@features/ui/icons';
import { compact } from 'lodash';

interface AddonsProps {
  onEdit: (id: number) => void;
  onView: (ids: number[]) => void;
  data: ProductOffer[];
  addonsSelectedIds: number[];
  setAddonsSelectedIds: (values: number[]) => void;
  addonsDisableIds: number[];
  setAddonsDisableIds: (values: number[]) => void;
}
export const Addons: FC<AddonsProps> = ({
  onEdit,
  onView,
  data,
  addonsSelectedIds,
  setAddonsSelectedIds,
  addonsDisableIds,
  setAddonsDisableIds,
}) => {
  const handleEdit = useCallback(
    (id?: number) => {
      if (!id) return;
      onEdit(id);
    },
    [onEdit]
  );
  const handleView = useCallback(
    (ids?: number[]) => {
      onView(ids ?? []);
    },
    [onView]
  );

  const handleSelectedView = useCallback(
    (id?: number) => {
      if (!id) return;
      const findId = addonsSelectedIds.find((i) => i === id);

      if (findId) {
        setAddonsSelectedIds(addonsSelectedIds.filter((item) => item !== id));

        return;
      }
      setAddonsSelectedIds([...addonsSelectedIds, id]);
    },
    [addonsSelectedIds, setAddonsSelectedIds]
  );

  const handleSelectedDisable = useCallback(
    (id?: number) => {
      if (!id) return;
      const findId = addonsDisableIds.find((i) => i === id);

      if (findId) {
        setAddonsDisableIds(addonsDisableIds.filter((item) => item !== id));

        return;
      }
      setAddonsDisableIds([...addonsDisableIds, id]);
    },
    [addonsDisableIds, setAddonsDisableIds]
  );

  const columns = useMemo(
    (): TColumn<ProductOffer>[] => [
      {
        title: (
          <div className={'ml-10'}>
            <p>#</p>
          </div>
        ),
        render: (item, index) => (
          <div className={'flex gap-4 items-center w-20'}>
            <Checkbox
              checked={addonsSelectedIds.includes(item?.id ?? 0)}
              className="size-6"
              onCheckedChange={() => handleSelectedView(item?.id)}
            />
            <p>{(index ?? 0) + 1}</p>
          </div>
        ),
      },
      {
        title: 'Title',
        className: 'w-full',
        render: (item) => <p>{item?.title}</p>,
      },
      {
        title: <span className="whitespace-nowrap">View Accounts</span>,
        className: 'min-w-[140px] whitespace-nowrap',
        render: (item) => (
          <div className={'ml-5 w-[100px]'}>
            <ViewButton
              iconComponent={<EyeV2Icon />}
              visible={false}
              onClick={() => handleView(compact(item?.addon_packages))}
            />
          </div>
        ),
      },
      {
        title: 'Disable',
        render: (item) => (
          <div className={'ml-3.5 w-20'}>
            <Checkbox
              checked={addonsDisableIds.includes(item?.id ?? 0)}
              className="size-6"
              onCheckedChange={() => handleSelectedDisable(item?.id)}
            />
          </div>
        ),
      },
      {
        title: '',
        render: (item) => (
          <Button
            size={'sm'}
            variant={'secondary'}
            onClick={() => handleEdit(item?.id)}
          >
            Edit
          </Button>
        ),
      },
    ],
    [
      addonsDisableIds,
      addonsSelectedIds,
      handleEdit,
      handleSelectedDisable,
      handleSelectedView,
      handleView,
    ]
  );

  return (
    <Card className={'mt-[27px] pl-[18px] pr-[24px]'}>
      <RadixTable columns={columns} data={data} />
    </Card>
  );
};
